// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Dialog for OIDC users to set (or enter) a vault passphrase.
 *
 * OIDC users (e.g. Google login) don't have a password, so they
 * can't derive a vault key automatically. They must create a separate
 * "vault passphrase" which is:
 *   1. Hashed with bcrypt and stored server-side (for verification)
 *   2. Run through Argon2id(passphrase, userUUID) to derive the 32-byte
 *      vault key (same as password-based users)
 *
 * This dialog appears when `has_vault_passphrase` is false and the user
 * has cloud storage connected.
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { KeyRound, Loader2, X } from 'lucide-react';
import { PasswordInput } from '@/app/components/password-input';
import { useAuthStore } from '@/app/stores/auth-store';
import { useSetupVaultPassphrase, useUnlockVault } from '@/app/features/storage';
import { deriveVaultKey, bytesToBase64 } from '@/lib/crypto';

interface VaultPassphraseDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog requests to close. */
  onOpenChange: (open: boolean) => void;
  /** "setup" for first-time passphrase creation, "unlock" for returning users. */
  mode: 'setup' | 'unlock';
}

export function VaultPassphraseDialog({
  open,
  onOpenChange,
  mode,
}: VaultPassphraseDialogProps) {
  const { user } = useAuthStore();
  const setupMutation = useSetupVaultPassphrase();
  const unlockMutation = useUnlockVault();

  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [error, setError] = useState('');
  const [deriving, setDeriving] = useState(false);

  const isPending =
    deriving || setupMutation.isPending || unlockMutation.isPending;

  const isSetup = mode === 'setup';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError('');

    if (passphrase.length < 8) {
      setError('Passphrase must be at least 8 characters.');
      return;
    }

    if (isSetup && passphrase !== confirmPassphrase) {
      setError('Passphrases do not match.');
      return;
    }

    try {
      setDeriving(true);
      const keyBytes = await deriveVaultKey(passphrase, user.id);
      const keyBase64 = bytesToBase64(keyBytes);
      setDeriving(false);

      if (isSetup) {
        await setupMutation.mutateAsync({
          passphrase,
          decryptionKeyBase64: keyBase64,
        });
      } else {
        await unlockMutation.mutateAsync(keyBase64);
      }

      // Success — close dialog and reset state.
      setPassphrase('');
      setConfirmPassphrase('');
      onOpenChange(false);
    } catch (err) {
      setDeriving(false);
      setError(
        err instanceof Error ? err.message : 'Failed to set passphrase.',
      );
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card-solid p-6 shadow-lg focus:outline-none">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <KeyRound className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <Dialog.Title className="text-[15px] font-semibold text-foreground">
                {isSetup ? 'Create Vault Passphrase' : 'Unlock Vault'}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                {isSetup
                  ? 'Your data is encrypted before syncing to the cloud. Create a passphrase to protect your encryption key.'
                  : 'Enter your vault passphrase to decrypt your data.'}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <div>
              <label
                htmlFor="vault-passphrase"
                className="block text-[12px] font-medium text-muted-foreground"
              >
                {isSetup ? 'Passphrase' : 'Vault Passphrase'}
              </label>
              <PasswordInput
                id="vault-passphrase"
                reveals="passphrase"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                placeholder="At least 8 characters"
                autoFocus
                disabled={isPending}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/60 transition-colors focus:border-primary focus:outline-none disabled:opacity-50"
              />
            </div>

            {isSetup && (
              <div>
                <label
                  htmlFor="vault-passphrase-confirm"
                  className="block text-[12px] font-medium text-muted-foreground"
                >
                  Confirm Passphrase
                </label>
                <PasswordInput
                  id="vault-passphrase-confirm"
                  reveals="confirmed passphrase"
                  value={confirmPassphrase}
                  onChange={(e) => setConfirmPassphrase(e.target.value)}
                  placeholder="Enter passphrase again"
                  disabled={isPending}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/60 transition-colors focus:border-primary focus:outline-none disabled:opacity-50"
                />
              </div>
            )}

            {error && (
              <p className="text-[12px] text-destructive">{error}</p>
            )}

            {isSetup && (
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                This passphrase is used to derive your encryption key. If
                you forget it, your cloud-synced data cannot be recovered.
                Store it safely.
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={isPending}
                  className="rounded-md px-3 py-2 text-[14px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={isPending || passphrase.length < 8}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              >
                {isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {deriving ? 'Deriving key...' : 'Saving...'}
                  </>
                ) : isSetup ? (
                  'Create Passphrase'
                ) : (
                  'Unlock'
                )}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
